import {
  buildPublicVehicleUrl,
  resolveCanonicalVehiclePublicUrl,
  toPublicVehicleLinkInput,
} from "../automapListing/resolveCanonicalVehiclePublicUrl";

/**
 * Extract the vehicle listing id segment from a public vehicle detail path.
 */
export function extractMobilityIdFromPublicVehiclePath(url) {
  if (!url) return null;
  const match = String(url).match(/\/vehicles\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Validate that a dealer-facing public URL uses the mobility listing id in the route.
 */
export function isCanonicalDealerPublicUrl({
  canonicalPublicUrl,
  mobilityListingId = null,
  marketListingId = null,
} = {}) {
  if (!canonicalPublicUrl) return false;
  const segment = extractMobilityIdFromPublicVehiclePath(canonicalPublicUrl);
  if (!segment) return false;
  if (marketListingId && segment === marketListingId) return false;
  if (mobilityListingId && segment !== mobilityListingId) return false;
  return true;
}

/**
 * Resolve the canonical public URL for a dealer inventory listing (M5/M7).
 * Prefers API `canonicalPublicUrl`, then builds from `mobilityListingId`.
 * Never uses dealer `id` / `marketListingId` as the public route identifier.
 */
export function getDealerListingPublicUrl(
  listing,
  { locale = null, slug = "automap", manifest = null } = {}
) {
  if (!listing || typeof listing !== "object") return null;

  const identity = toPublicVehicleLinkInput({
    ...listing,
    marketListingId: listing.marketListingId || listing.id || null,
  });

  if (identity.canonicalPublicUrl) {
    if (
      isCanonicalDealerPublicUrl({
        canonicalPublicUrl: identity.canonicalPublicUrl,
        mobilityListingId: identity.mobilityListingId,
        marketListingId: identity.marketListingId,
      })
    ) {
      return identity.canonicalPublicUrl;
    }
  }

  return resolveCanonicalVehiclePublicUrl({
    mobilityListingId: identity.mobilityListingId,
    locale,
    slug,
    manifest,
  });
}

/**
 * @param {object} listing
 * @param {(key: string) => string} t
 * @returns {string|null}
 */
export function getDealerListingPublicUrlUnavailableMessage(listing, t) {
  if (!listing) return t("inventory.publicListingUnavailable");
  if (listing.status && listing.status !== "active") {
    return t("inventory.publicListingUnavailable");
  }
  return t("inventory.publicListingSyncPending");
}

export function buildDealerListingPublicLinkAnalytics(listing, publicUrl) {
  const identity = toPublicVehicleLinkInput({
    ...listing,
    marketListingId: listing?.marketListingId || listing?.id || null,
  });
  return {
    marketListingId: identity.marketListingId,
    mobilityListingId: identity.mobilityListingId,
    canonicalVehicleId: identity.canonicalVehicleId,
    canonicalPublicUrl: identity.canonicalPublicUrl || publicUrl || null,
    listingId: identity.mobilityListingId || identity.marketListingId || null,
  };
}

// Re-export for dealer surfaces that build paths directly.
export { buildPublicVehicleUrl };
